import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { syncToMailerLite } from "@/lib/mailerlite";

const HeroSection = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [gdprConsent, setGdprConsent] = useState(false);
  const [showGdprDialog, setShowGdprDialog] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email address");
      return;
    }
    if (!gdprConsent) {
      setShowGdprDialog(true);
      return;
    }
    await submitEmail();
  };

  const submitEmail = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from("newsletter_subscribers")
        .insert([{ email: email.trim(), source: "Adventure List" }]);
      if (error) {
        if (error.code === "23505") {
          toast.info("You're already on the list!");
        } else {
          throw error;
        }
      } else {
        syncToMailerLite(email.trim(), "Adventure List");
        setEmail("");
        navigate("/thank-you");
      }
    } catch (error) {
      console.error("Error joining list:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section 
      id="hero" 
      className="relative min-h-screen flex items-center justify-center pt-0 paper-texture overflow-hidden"
    >
      {/* Background Image */}
      <img
        src="https://images.unsplash.com/photo-1551632811-561732d1e306?w=1280&q=40&fm=webp"
        srcSet="https://images.unsplash.com/photo-1551632811-561732d1e306?w=480&q=40&fm=webp 480w, https://images.unsplash.com/photo-1551632811-561732d1e306?w=768&q=40&fm=webp 768w, https://images.unsplash.com/photo-1551632811-561732d1e306?w=1280&q=40&fm=webp 1280w"
        sizes="100vw"
        alt=""
        aria-hidden="true"
        width={1280}
        height={853}
        fetchPriority="high"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-cream/85 via-cream/70 to-cream/95" />

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-16 md:py-24">
        <div className="max-w-4xl mx-auto text-center md:text-left">
          <p className="font-mono text-sm uppercase tracking-widest text-accent mb-6 animate-fade-in">
            By Dr Rebecca Ryell — UK GP, global family traveller and adventure travel coach based in South Wales.
          </p>

          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-primary mb-6 leading-tight animate-fade-in" style={{ animationDelay: "0.1s" }}>
            Helping Families Travel Bravely — With Confidence, Purpose and Deeper Connection
          </h1>

          <p className="text-lg md:text-xl text-foreground max-w-2xl md:mx-0 mx-auto mb-4 animate-fade-in" style={{ animationDelay: "0.2s" }}>
            Family adventure travel coaching for thoughtful parents who want to raise resilient, world-ready children through meaningful travel experiences.
          </p>

          {/* CTA Form */}
          <div className="max-w-md md:mx-0 mx-auto animate-fade-in" style={{ animationDelay: "0.3s" }}>
            <form onSubmit={handleSubmit} className="flex flex-col gap-2" data-form="adventure-list">
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex-1 relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-12 py-6 text-base bg-card/90 border-border"
                    required
                  />
                </div>
                <Button 
                  type="submit"
                  disabled={isLoading}
                  size="lg"
                  className="bg-accent hover:bg-accent/90 text-accent-foreground font-mono uppercase tracking-wider shadow-lg hover:shadow-xl transition-all py-6 hero-join-btn"
                >
                  {isLoading ? "Joining..." : "Get the Newsletter"}
                </Button>
              </div>
            </form>

            <AlertDialog open={showGdprDialog} onOpenChange={setShowGdprDialog}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-display text-primary">Before you join…</AlertDialogTitle>
                  <AlertDialogDescription className="text-foreground leading-relaxed">
                    By signing up you agree to our{" "}
                    <a href="/privacy" className="text-accent underline" target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </a>{" "}
                    and to receive adventure updates. You can unsubscribe at any time.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="font-mono text-sm">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-accent hover:bg-accent/90 text-accent-foreground font-mono text-sm"
                    onClick={() => {
                      setGdprConsent(true);
                      submitEmail();
                    }}
                  >
                    I Agree — Get the Newsletter
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>

          <p className="font-display text-base md:text-lg text-accent font-medium max-w-xl md:mx-0 mx-auto mt-8 italic animate-fade-in" style={{ animationDelay: "0.4s" }}>
            Trade the "what if" for the "remember when."
          </p>

        </div>
      </div>

      <div className="coffee-stain w-32 h-32 -bottom-8 -right-8 hidden md:block" style={{ transform: "rotate(15deg)" }} />
    </section>
  );
};

export default HeroSection;